import { ICadastroRgState } from 'store/modules/api/iirgd/cadastroRg/types';
import { ConsultarUsuariosAcessoState } from 'store/modules/api/sgu/loginUnico/types';
import { EventosPPTContabilizacaoRequest } from 'store/modules/api/utils/eventosPPT/contabilizacao/types';
import { treatRequestEventsContabilizacaoPPT } from 'utils/functions/treatEventosPPTRequest';
import { AtendimentoState } from 'store/modules/api/sgu/atendimento/types';
import { IFormCadastroRg } from '../CadastroRg/form';
import { getPagamentoTaxas } from './Buttons/ImprimirProtocolo/functions';

const convertBooleanToString = (option: boolean) => {
	const affirmative = 'SIM';
	const negative = 'NAO';

	return option ? affirmative : negative;
};

export function formatarContabilizacao(
	atendimento: AtendimentoState,
	cadastroRg: ICadastroRgState,
	login: ConsultarUsuariosAcessoState,
	restricaoCriminal: boolean,
	tipoCili: string | null,
	resultadoDigitais: boolean,
	primeiraViaCin: boolean,
): EventosPPTContabilizacaoRequest {
	const { form, tipo, perguntas } = cadastroRg;
	const taxas = getPagamentoTaxas(cadastroRg);
	const eventoPPTDefaultData = treatRequestEventsContabilizacaoPPT(
		atendimento,
		login,
		form.principal?.cpf || '',
		'IIRGD',
		396,
		'',
	);

	const getTypeAttendency = (
		isRGPrimeiraVia: boolean,
		isRGSegundaVia: boolean,
		isPrimeiraViaCin: boolean,
		dni?: string | null,
	) => {
		if (dni !== '10977' && isRGPrimeiraVia) {
			return {
				typeAttendency: 'iirgd_rg_primeira_via',
				serviceId: 5,
				description: 'primeira via rg',
			};
		}
		if (isPrimeiraViaCin) {
			return {
				typeAttendency: 'iirgd_cin_primeira_via',
				serviceId: 14,
				description: 'cin primeira via',
			};
		}
		if (dni !== '10977' && isRGSegundaVia) {
			return {
				typeAttendency: 'iirgd_rg_segunda_via',
				serviceId: 6,
				description: 'segunda via rg',
			};
		}
		if (dni === '10977' && !isPrimeiraViaCin) {
			return {
				typeAttendency: 'iirgd_cin_segunda_via',
				serviceId: 15,
				description: 'cin segunda via',
			};
		}
		return { typeAttendency: '', serviceId: 0 };
	};

	const getTypeIsencao = () => {
		if (form.observacoes.segundaVia?.isento2Via10952) {
			return 'IDADE';
		}
		if (form.observacoes.segundaVia?.declaracaoPobreza) {
			return 'POBREZA';
		}
		if (cadastroRg.form.principal?.dni !== '10977' && tipo.primeiraVia) {
			return 'PRIMEIRA_VIA';
		}
		if (form.observacoes.segundaVia?.isento2Via062) {
			return 'CORRECAO';
		}
		return 'SEM_ISENCAO';
	};

	const ciliSimplificada =
		tipoCili !== null ||
		tipoCili === 'A' ||
		tipoCili === 'B' ||
		tipoCili === 'C';

	const { typeAttendency, serviceId, description } = getTypeAttendency(
		tipo.primeiraVia,
		tipo.segundaVia,
		primeiraViaCin,
		cadastroRg.form.principal?.dni,
	);

	return {
		...eventoPPTDefaultData,
		tipo: typeAttendency,
		dados: {
			...eventoPPTDefaultData.dados,
			identificacao:
				atendimento.recuperarAtendimentoIntegrador.data?.cpfCidadao ||
				form.principal?.cpf ||
				form.principal?.numeroRg ||
				'',
			id_servico: serviceId,
			menor_idade: convertBooleanToString(!cadastroRg.info.maior),
			kit_familia: convertBooleanToString(
				form.principal?.entrega === 'K' || form.principal?.entrega === 'KC',
			),
			reuso: {
				confronto_realizado: convertBooleanToString(resultadoDigitais),
				dedos: convertBooleanToString(perguntas.todosDedos),
				erro_reuso: convertBooleanToString(!resultadoDigitais),
			},
			naturalizado: convertBooleanToString(!!form.principal?.pMinLei1),
			criminal: convertBooleanToString(restricaoCriminal),
			inclusao_cpf: convertBooleanToString(!!form.principal?.inscricaoCpf),
			inclusao_nome_social: convertBooleanToString(
				!!form.principal?.nomeSocial,
			),
			cili_simplificada: convertBooleanToString(ciliSimplificada),
			cpf_operador: eventoPPTDefaultData.dados.identificacao,
			id_posto: eventoPPTDefaultData.dados.id_localidade,
			desc_posto: eventoPPTDefaultData.dados.desc_localidade,
			tipo_isencao: getTypeIsencao(),
			entrega_posto: convertBooleanToString(form.principal?.entrega === 'P'),
			entrega_correio: convertBooleanToString(form.principal?.entrega !== 'P'),
			dare_vinculada: convertBooleanToString(
				!!taxas.pgtoTxCorreio || !!taxas.pgtoTx2via,
			),
			desc_servico: description,
		},
	};
}

function getFieldsReusoParcial(
	original: IFormCadastroRg,
	formulario: IFormCadastroRg,
): string {
	/**
	 * Campos a serem validados:
	 * Principal: Nome
	 * Principal: Filiação 1
	 * Principal: Nome do Mãe
	 * Principal: Sexo
	 * Principal: Data de nascimento
	 * Principal: Município
	 *
	 * Documento apresentado: Tipo de documento
	 * Documento apresentado: Comarca
	 * Documento apresentado: Cartório
	 * Documento apresentado: Livro
	 * Documento apresentado: Folha
	 * Documento apresentado: Número
	 * Documento apresentado: Data do documento
	 * Documento apresentado: Port. Min-Lei
	 *
	 * Dados adicionais - Cidadão: Estado Cívil
	 * Dados adicionais - Cidadão: Cutis
	 * Dados adicionais - Cidadão: Cabelos
	 * Dados adicionais - Cidadão: Olhos
	 * Dados adicionais - Cidadão: Profissão
	 *
	 * Dados adicionais - RG: CPF
	 * Dados adicionais - RG: PIS/NIS
	 * Dados adicionais - RG: PASEP
	 */

	const campos: string[] = [];

	if (formulario.nome !== original.nome) {
		campos.push('nome');
	}
	if (formulario.nomePai !== original.nomePai) {
		campos.push('nomePai');
	}
	if (formulario.nomeMae !== original.nomeMae) {
		campos.push('nomeMae');
	}
	if (formulario.sexo !== original.sexo) {
		campos.push('sexo');
	}
	if (formulario.genero !== original.genero) {
		campos.push('genero');
	}
	if (formulario.dataNascimento !== original.dataNascimento) {
		campos.push('dataNascimento');
	}
	if (formulario.codigoNaturalidade !== original.codigoNaturalidade) {
		campos.push('codigoNaturalidade');
	}
	if (formulario.dbas !== original.dbas) {
		campos.push('dbas');
	}
	if (formulario.comarca !== original.comarca) {
		campos.push('comarca');
	}
	if (formulario.cartorio !== original.cartorio) {
		campos.push('cartorio');
	}
	if (formulario.livro !== original.livro) {
		campos.push('livro');
	}
	if (formulario.folha !== original.folha) {
		campos.push('folha');
	}
	if (formulario.numero !== original.numero) {
		campos.push('numero');
	}
	if (formulario.dataDocumento !== original.dataDocumento) {
		campos.push('dataDocumento');
	}
	if (formulario.pMinLei1 !== original.pMinLei1) {
		campos.push('pMinLei1');
	}
	if (formulario.pMinLei2 !== original.pMinLei2) {
		campos.push('pMinLei2');
	}
	if (formulario.estadoCivil !== original.estadoCivil) {
		campos.push('estadoCivil');
	}
	if (formulario.cutis !== original.cutis) {
		campos.push('cutis');
	}
	if (formulario.cabelos !== original.cabelos) {
		campos.push('cabelos');
	}
	if (formulario.olhos !== original.olhos) {
		campos.push('olhos');
	}
	if (formulario.profissao !== original.profissao) {
		campos.push('profissao');
	}
	if (formulario.cpf !== original.cpf) {
		campos.push('cpf');
	}
	if (formulario.pis !== original.pis) {
		campos.push('pis');
	}
	if (formulario.pasep !== original.pasep) {
		campos.push('pasep');
	}
	if (campos.length) {
		let novosCampos: string = '';
		campos.map((item: string, index: number) => {
			novosCampos += item + (index !== campos.length - 1 ? ',' : '');
			return novosCampos;
		});
		return `Campos alterados: ${novosCampos} - Confronto realizado com sucesso.`;
	}

	return 'Não teve alteração dos campos de Reuso Parcial';
}

function getFieldsReusoTotal(
	original: IFormCadastroRg,
	formulario: IFormCadastroRg,
): string {
	/**
	 * Campos a serem validados:
	 *
	 * Principal: Nome social
	 *
	 * Documento apresentado: Data do documento
	 *
	 * Dados adicionais - Cidadão: Altura
	 * Dados adicionais - Cidadão: Grau de instrução
	 * Dados adicionais - Cidadão: CEP
	 * Dados adicionais - Cidadão: Logradouro
	 * Dados adicionais - Cidadão: Número
	 * Dados adicionais - Cidadão: Complemento
	 * Dados adicionais - Cidadão: Bairro
	 * Dados adicionais - Cidadão: Munícipio
	 *
	 * Dados adicionais - Trabalho: CEP
	 * Dados adicionais - Trabalho: Logradouro
	 * Dados adicionais - Trabalho: Número
	 * Dados adicionais - Trabalho: Complemento
	 * Dados adicionais - Trabalho: Bairro
	 * Dados adicionais - Trabalho: Munícipio
	 */

	const campos: string[] = [];

	if (formulario.nomeSocial !== original.nomeSocial) {
		campos.push('nomeSocial');
	}
	if (formulario.dataDocumento !== original.dataDocumento) {
		campos.push('dataDocumento');
	}
	if (formulario.altura !== original.altura) {
		campos.push('altura');
	}
	if (formulario.grauInstrucao !== original.grauInstrucao) {
		campos.push('grauInstrucao');
	}
	if (formulario.cepResidencia !== original.cepResidencia) {
		campos.push('cepResidencia');
	}
	if (formulario.enderecoResidencia !== original.enderecoResidencia) {
		campos.push('enderecoResidencia');
	}
	if (formulario.numeroResidencia !== original.numeroResidencia) {
		campos.push('numeroResidencia');
	}
	if (formulario.bairroResidencia !== original.bairroResidencia) {
		campos.push('bairroResidencia');
	}
	if (formulario.complementoResidencia !== original.complementoResidencia) {
		campos.push('complementoResidencia');
	}
	if (formulario.complementoResidencia !== original.complementoResidencia) {
		campos.push('complementoResidencia');
	}
	if (
		formulario.codigoMunicipioResidencia !== original.codigoMunicipioResidencia
	) {
		campos.push('codigoMunicipioResidencia');
	}
	if (formulario.cepTrabalho !== original.cepTrabalho) {
		campos.push('cepTrabalho');
	}

	if (formulario.enderecoTrabalho !== original.enderecoTrabalho) {
		campos.push('enderecoTrabalho');
	}
	if (formulario.numeroTrabalho !== original.numeroTrabalho) {
		campos.push('numeroTrabalho');
	}
	if (formulario.complementoTrabalho !== original.complementoTrabalho) {
		campos.push('complementoTrabalho');
	}
	if (formulario.bairroTrabalho !== original.bairroTrabalho) {
		campos.push('bairroTrabalho');
	}

	if (formulario.codigoMunicipioTrabalho !== original.codigoMunicipioTrabalho) {
		campos.push('codigoMunicipioTrabalho');
	}

	if (formulario.descMunicipioTrabalho !== original.descMunicipioTrabalho) {
		campos.push('descMunicipioTrabalho');
	}
	if (formulario.solicitacao !== original.solicitacao) {
		campos.push('solicitacao');
	}
	if (formulario.foneResidencia !== original.foneResidencia) {
		campos.push('foneResidencia');
	}
	if (formulario.foneComercial !== original.foneComercial) {
		campos.push('foneComercial');
	}
	if (formulario.celular !== original.celular) {
		campos.push('celular');
	}
	if (formulario.operadora !== original.operadora) {
		campos.push('operadora');
	}
	if (formulario.email !== original.email) {
		campos.push('email');
	}

	if (formulario.tituloEleitor !== original.tituloEleitor) {
		campos.push('tituloEleitor');
	}

	if (formulario.email !== original.email) {
		campos.push('email');
	}

	if (formulario.tituloEleitor !== original.tituloEleitor) {
		campos.push('tituloEleitor');
	}
	if (formulario.ctpsNum !== original.ctpsNum) {
		campos.push('ctpsNum');
	}
	if (formulario.ctpsSerie !== original.ctpsSerie) {
		campos.push('ctpsSerie');
	}
	if (formulario.ctpsSerieUf !== original.ctpsSerieUf) {
		campos.push('ctpsSerieUf');
	}
	if (formulario.certidaoMilitar !== original.certidaoMilitar) {
		campos.push('certidaoMilitar');
	}
	if (formulario.cnh !== original.cnh) {
		campos.push('cnh');
	}
	if (formulario.nascSaude !== original.nascSaude) {
		campos.push('nascSaude');
	}
	if (formulario.identProf1 !== original.identProf1) {
		campos.push('identProf1');
	}
	if (formulario.orgaoEmissor1 !== original.orgaoEmissor1) {
		campos.push('orgaoEmissor1');
	}
	if (formulario.identProf2 !== original.identProf2) {
		campos.push('identProf2');
	}
	if (formulario.orgaoEmissor2 !== original.orgaoEmissor2) {
		campos.push('orgaoEmissor2');
	}
	if (formulario.identProf3 !== original.identProf3) {
		campos.push('identProf3');
	}
	if (formulario.orgaoEmissor3 !== original.orgaoEmissor3) {
		campos.push('orgaoEmissor3');
	}
	if (campos.length) {
		let novosCampos: string = '';
		campos.map((item: string, index: number) => {
			novosCampos += item + (index !== campos.length - 1 ? ',' : '');
			return novosCampos;
		});
		return `Campos alterados: ${novosCampos} - Confronto realizado com sucesso.`;
	}

	return 'Não teve alteração dos campos de Reuso Total';
}

export function getMensagemReuso(cadastro: ICadastroRgState): string {
	let response: string = '';

	if (
		cadastro.tipo.reusoParcial &&
		cadastro.original &&
		cadastro.form.principal
	) {
		response = getFieldsReusoParcial(
			cadastro.original,
			cadastro.form.principal,
		);
	}
	if (
		cadastro.tipo.reusoTotal &&
		cadastro.original &&
		cadastro.form.principal
	) {
		response = getFieldsReusoTotal(cadastro.original, cadastro.form.principal);
	}
	return response;
}
